import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';

import { Header } from '@99designs/fullscreen/components/Header';
import { ButtonListItem } from '@99designs/react-components/components/ButtonList';
import IconButton from '@99designs/react-components/components/IconButton';
import Icon from '@99designs/react-components/components/Icon';
import LinkWithIcon from '@99designs/react-components/components/LinkWithIcon';
import Desktop from '@99designs/react-components/components/Desktop';
import Mobile from '@99designs/react-components/components/Mobile';
import bemComponent from '@99designs/utils/app/bemComponent';
import classNames from 'classnames';

import { ConnectedFeedbackControls } from './FeedbackControls';
import ViewControls from './ViewControls';
import DesignDetailsPagination from './DesignDetailsPagination';

import connectToStores from 'fluxible-addons-react/connectToStores';
import DesignCollectionStore from '../../stores/DesignCollectionStore';
import DesignSketchesStore from '../../stores/DesignSketchesStore';

const HeaderItem = bemComponent('fullscreen-header__item--design-details');
const Pagination = injectIntl(DesignDetailsPagination);

export default class DesignDetailsHeader extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            idle: false,
        };

        this.setIdleState = this.setIdleState.bind(this);
    }

    setIdleState(state) {
        this.setState({ idle: state });
    }

    renderNavigation() {
        if (this.props.isInline) {
            return (
                <HeaderItem>
                    <Pagination
                        key={this.props.design.designId}
                        currentPage={this.props.currentPage}
                        onNext={this.props.goToNext}
                        onPrevious={this.props.goToPrevious}
                        totalPages={this.props.totalPages}
                    />
                </HeaderItem>
            );
        } else {
            const total = this.props.totalPages;
            return this.props.seeMoreDesignsUrl ? (
                <LinkWithIcon
                    href={this.props.seeMoreDesignsUrl}
                    icon={<Icon variations={['grid-1']} />}
                >
                    <FormattedMessage
                        id={total > 1 ? 'see_all_designs' : 'see_other_designs'}
                        values={{ number: total }}
                    />
                </LinkWithIcon>
            ) : (
                <div className="logo-99designs">
                    <a href="/" className="logo-99designs__logo">
                        99designs
                    </a>
                </div>
            );
        }
    }

    renderCloseButton() {
        return (
            <HeaderItem>
                <ButtonListItem>
                    <IconButton
                        key="closeBtn"
                        title={this.context.intl.formatMessage({ id: 'close_ddo_tooltip' })}
                        onClick={this.props.closeOverlayHandler}
                    >
                        <Icon variations={['x-3']} />
                    </IconButton>
                </ButtonListItem>
            </HeaderItem>
        );
    }

    render() {
        return (
            <Header
                variations={
                    this.props.isPresentationMode && this.state.idle
                        ? ['inactive', 'design-details']
                        : ['design-details']
                }
            >
                {this.renderNavigation()}

                {this.context.scribblesEnabled &&
                    this.props.design.permission.canViewSketches && (
                        <Desktop>
                            <StatusIndicator state={this.props.sketchState} />
                        </Desktop>
                    )}

                {this.context.scribblesEnabled &&
                    this.props.design.permission.canSketch &&
                    !this.props.isPresentationMode && (
                        <Desktop>
                            <ConnectedFeedbackControls
                                designId={this.props.design.designId}
                                feedbackMode={this.props.feedbackMode}
                                toggleFeedbackMode={this.props.toggleFeedbackMode}
                            />
                        </Desktop>
                    )}

                {this.props.design.permission.canView && (
                    <Desktop>
                        <ViewControls
                            setIdleState={this.setIdleState}
                            handleMinimize={this.props.handleMinimize}
                            handleMaximize={this.props.handleMaximize}
                            isPresentationMode={this.props.isPresentationMode}
                            zoomIn={this.props.zoomIn}
                            zoomOut={this.props.zoomOut}
                        />
                    </Desktop>
                )}

                {this.props.isInline && <Mobile>{this.renderCloseButton()}</Mobile>}
            </Header>
        );
    }
}

DesignDetailsHeader.contextTypes = {
    intl: PropTypes.object.isRequired,
    scribblesEnabled: PropTypes.bool.isRequired,
};

DesignDetailsHeader.propTypes = {
    design: PropTypes.object,
    isInline: PropTypes.bool,
    isPresentationMode: PropTypes.bool,
    handleMaximize: PropTypes.func,
    handleMinimize: PropTypes.func,
    goToNext: PropTypes.func,
    goToPrevious: PropTypes.func,
    closeOverlayHandler: PropTypes.func,
    totalPages: PropTypes.number,
    currentPage: PropTypes.number,
    seeMoreDesignsUrl: PropTypes.string,
    zoomIn: PropTypes.func,
    zoomOut: PropTypes.func,
    feedbackMode: PropTypes.string.isRequired,
    toggleFeedbackMode: PropTypes.func.isRequired,
    sketchState: PropTypes.string,
};

DesignDetailsHeader.defaultProps = {
    isInline: true,
    isPresentationMode: false,
    handleMinimize() {},
    handleMaximize() {},
    goToPrevious() {},
    goToNext() {},
    closeOverlayHandler() {},
    totalPages: 1,
    currentPage: 1,
    zoomIn: null,
    zoomOut: null,
    sketchState: '',
};

export const ConnectedDesignDetailsHeader = connectToStores(
    DesignDetailsHeader,
    [DesignCollectionStore, DesignSketchesStore],
    (context, props) => {
        const collectionStore = context.getStore(DesignCollectionStore);
        return {
            totalPages: collectionStore.totalDesignCount,
            currentPage: collectionStore.getCurrentPage(props.design.designId),
            sketchState: context
                .getStore(DesignSketchesStore)
                .getDesignSketchState(props.design.designId),
        };
    }
);

ConnectedDesignDetailsHeader.propTypes = DesignDetailsHeader.propTypes;
ConnectedDesignDetailsHeader.defaultProps = DesignDetailsHeader.defaultProps;

const StatusIndicator = ({ state }) => {
    let message;
    switch (state) {
        case 'loading':
            message = <FormattedMessage id="sketches_loading" />;
            break;
        case 'saving':
            message = <FormattedMessage id="sketches_saving" />;
            break;
        case 'saved':
            message = <FormattedMessage id="sketches_saved" />;
            break;
        case 'error':
            message = <FormattedMessage id="sketches_error" />;
            break;
        default:
            message = null;
            break;
    }

    const classes = classNames({
        'fullscreen-header__item--status': true,
        'fullscreen-header__item--saved': state === 'saved',
    });

    return (
        <div key={state} className={classes}>
            {message}
        </div>
    );
};

DesignDetailsHeader.propTypes = {
    state: PropTypes.string,
};



// WEBPACK FOOTER //
// ./~/@99designs/ddo/components/Header/DesignDetailsHeader.jsx